import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} nav - The navigation container.
 * @property {HTMLButtonElement[]} tabs - The tab buttons.
 * @property {HTMLElement[]} panels - The content panels.
 * @property {HTMLElement} contentWrapper - The content wrapper.
 * @property {HTMLElement} sizer - The sizer element for min-height calculation.
 */

/**
 * Dynamic tabs component with fade-in transition.
 * Calculates min-height based on content to prevent layout shifts.
 *
 * @extends Component<Refs>
 */
class DynamicTabsComponent extends Component {
  /** @type {number} */
  #activeIndex = 0;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupTabs();
    this.#setupResizeObserver();
    this.#updateMinHeight();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroyResizeObserver();
  }

  /**
   * Sets up click handlers for tab buttons.
   */
  #setupTabs() {
    const tabs = this.refs.tabs;
    if (!tabs) return;

    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        const index = parseInt(tab.dataset.tabIndex || '0', 10);
        this.#switchTab(index);
      });
    }
  }

  /**
   * Sets up ResizeObserver to recalculate min-height on content changes.
   */
  #setupResizeObserver() {
    const panels = this.refs.panels;
    if (!panels) return;

    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateMinHeight();
    });

    for (const panel of panels) {
      this.#resizeObserver.observe(panel);
    }
  }

  /**
   * Destroys the ResizeObserver.
   */
  #destroyResizeObserver() {
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
  }

  /**
   * Switches to a new tab with fade transition.
   * @param {number} newIndex - The index of the tab to switch to.
   */
  #switchTab(newIndex) {
    if (newIndex === this.#activeIndex) return;

    const tabs = this.refs.tabs;
    const panels = this.refs.panels;
    if (!tabs || !panels) return;

    const oldTab = tabs[this.#activeIndex];
    const newTab = tabs[newIndex];
    const oldPanel = panels[this.#activeIndex];
    const newPanel = panels[newIndex];

    if (!oldTab || !newTab || !oldPanel || !newPanel) return;

    oldTab.classList.remove('dynamic-tabs__tab--active');
    oldTab.setAttribute('aria-selected', 'false');
    oldPanel.classList.remove('dynamic-tabs__panel--active');
    oldPanel.setAttribute('aria-hidden', 'true');

    newTab.classList.add('dynamic-tabs__tab--active');
    newTab.setAttribute('aria-selected', 'true');
    newPanel.classList.add('dynamic-tabs__panel--active');
    newPanel.setAttribute('aria-hidden', 'false');

    this.#activeIndex = newIndex;
    this.#updateMinHeight();
  }

  /**
   * Updates the min-height of the content wrapper based on the active panel.
   */
  #updateMinHeight() {
    const panels = this.refs.panels;
    const sizer = this.refs.sizer;
    const contentWrapper = this.refs.contentWrapper;

    if (!panels || !sizer || !contentWrapper) return;

    const activePanel = panels[this.#activeIndex];
    if (!activePanel) return;

    const height = activePanel.scrollHeight;
    sizer.style.minHeight = `${height}px`;
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#activeIndex = 0;
    this.#setupTabs();
    this.#updateMinHeight();
  }
}

if (!customElements.get('dynamic-tabs')) {
  customElements.define('dynamic-tabs', DynamicTabsComponent);
}
